import React, { useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import BaseModal from '@/components/reusables/Modals/BaseModal';
import { stopPropagate } from '@/helpers/utils';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import BaseNotFound from '@/components/reusables/BaseNotFound';
import BaseInputsGrid from '@/components/reusables/BaseInputsGrid';
import BaseButton from '@/components/reusables/BaseButton';
import BatchGroup from './BatchGroup';
import { useTranslation } from 'react-i18next';

const formScheme = z.object({
    product: z.object({
        quantity: z.coerce.number(),
        batch_numbers: z.array(
            z.object({
                batch_number: z.string(),
                expiration_date: z.instanceof(dayjs as unknown as typeof Dayjs).refine(value => value.isValid(), {
                    message: "Invalid date",
                }).optional()
            })
        )
    })
});

export type BatchNumbersScheme = z.infer<typeof formScheme>


interface BatchNumbersModalProps {
    isOpen: boolean;
    onClose: () => void;

    product: {
        quantity: number;
        batch_numbers?: {
            batch_number: string;
            expiration_date?: dayjs.Dayjs;
        }[]
    };

    onSave?: (data: BatchNumbersScheme) => void;

    preview?: boolean;
}

export default function BatchNumbersModal(props: BatchNumbersModalProps) {
    const { t } = useTranslation("", { keyPrefix: "bom.produceModal.modals.batchNumbersModal" });

    const { control, handleSubmit, setValue, reset } = useForm<BatchNumbersScheme>({
        resolver: zodResolver(formScheme)
    });

    const onSubmit = handleSubmit(data => {
        console.log(data);
        props.onSave?.(data);
    });

    useEffect(() => {
        if (props.isOpen) {
            const formReadyBatchNumbers = props.product.batch_numbers?.length ? props.product.batch_numbers : [{ batch_number: "" }];

            setValue("product.quantity", props.product.quantity);
            setValue("product.batch_numbers", formReadyBatchNumbers);
        } else {
            reset();
        }
    }, [props.isOpen, props.product.batch_numbers, setValue, reset]);

    return (
        <BaseModal
            isOpen={props.isOpen}
            onClose={props.onClose}
            width={900}
            padding="56px"
            position="center"
        >
            <form onSubmit={stopPropagate(onSubmit)} className="space-y-8">
            {
                props.product ?
                    <BatchGroup
                        control={control}
                        preview={props.preview}
                    />
                :
                <BaseNotFound text={"No batch numbers found"} height={250} />
            }
            <BaseInputsGrid cols={props.preview ? 1 : 2}>
                    <BaseButton
                        text={t("buttons.cancel")}
                        size="md"
                        buttonWidth="100%"
                        primaryOutlined
                        type="button"
                        onClick={props.onClose}
                    />

                    {
                        !props.preview && (
                            <BaseButton
                                text={t("buttons.attach")}
                                size="md"
                                buttonWidth="100%"
                            />
                        )
                    }
                </BaseInputsGrid>
            </form>
        </BaseModal>
    )
}
